import { toast } from "sonner";

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://edrock.org/api";
export const WS_BASE_URL =
  import.meta.env.VITE_WS_BASE_URL || "wss://chat.edrock.org/ws";
// export const API_BASE_URL = "http://127.0.0.1:8000/api";
// export const WS_BASE_URL = "ws://127.0.0.1:8000/ws";

// Types for API responses
export interface User {
  id: number;
  username: string;
  authenticated: boolean;
  user_image: string;
}

export interface Room {
  id: number;
  name: string;
  slug: string;
  image: string;
  created_at: string;
}

export interface Message {
  id: number;
  text_content: string;
  sender_id: number;
  timestamp: string;
  room: number;
  optimistic?: boolean;
}

export interface SendMessage {
  text_content: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface UserPayload {
  user_id: number;
  username: string;
  avatar_url: string;
}

interface GetChatTokenResponse {
  access_token: string;
  payload: UserPayload;
}

interface GeneralUserData {
  id: number;
  username: string;
  profile: {
    image: string;
  };
}

// WebSocket instance storage
let wsInstance: WebSocket | null = null;
let wsReadyPromise: Promise<void> | null = null;

// PURE API service functions - NO STORE DEPENDENCIES
export const apiService = {
  // Authentication - JUST RETURNS DATA
  async getUserStatus(): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/user_status/`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to get user status");
    }
    const user: User = await response.json();

    localStorage.setItem("username", user.username);
    localStorage.setItem("user_image", user.user_image);

    return user;
  },

  async getChatToken(): Promise<GetChatTokenResponse> {
    const response = await fetch(`${API_BASE_URL}/get_chat_token/`, {
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("Failed to get chat token");
    }
    return response.json();
  },

  async getRooms({ pageParam = 1 }): Promise<PaginatedResponse<Room[]>> {
    const url =
      pageParam === 1
        ? `https://chat.edrock.org/api/rooms/`
        : `https://chat.edrock.org/api/rooms/?page=${pageParam}`;
    const response = await fetch(url, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get rooms: ${response.statusText}`);
    }

    return response.json();
  },

  // Messages - JUST RETURNS DATA
  async getMessages(
    roomId: number,
    pageParam: number = 1
  ): Promise<PaginatedResponse<Message[]>> {
    const url =
      pageParam === 1
        ? `https://chat.edrock.org/api/messages/?room=${roomId}`
        : `https://chat.edrock.org/api/messages/?room=${roomId}&page=${pageParam}`; // Fix URL construction
    const response = await fetch(url, {
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("Failed to get messages");
    }
    return response.json();
  },
  // Messages - JUST RETURNS DATA
  async getUserData(userId: number): Promise<GeneralUserData> {
    const url = `${API_BASE_URL}/users/${userId}/`;
    const response = await fetch(url, {
      credentials: "include",
    });
    if (!response.ok) {
      toast.error(`Failed to get user data with ${userId} ID`);
    }
    return response.json();
  },

  // Initialize WebSocket connection
  async initWebSocket(roomSlug: string, token: string): Promise<WebSocket> {
    // Close existing connection if any
    if (wsInstance && wsInstance.readyState === WebSocket.OPEN) {
      wsInstance.close();
    }

    const wsUrl = `${WS_BASE_URL}/chat/${roomSlug}/?token=${token}`;
    wsInstance = new WebSocket(wsUrl);

    // Create a promise that resolves when WebSocket is ready
    wsReadyPromise = new Promise((resolve, reject) => {
      if (!wsInstance) {
        reject(toast.error("WebSocket instance not created"));
        return;
      }

      wsInstance.onopen = () => {
        resolve();
      };

      wsInstance.onerror = (error) => {
        console.error("WebSocket error:", error);
        reject(error);
      };
    });

    return wsInstance;
  },

  // Send message via WebSocket - ACCEPTS ALL NEEDED PARAMS
  async sendMessage(
    text_content: string,
    roomSlug: string,
    token?: string
  ): Promise<void> {
    if (!wsInstance || wsInstance.readyState !== WebSocket.OPEN) {
      if (!token) {
        throw new Error("Token required to reconnect WebSocket");
      }

      // Try to reconnect
      await apiService.initWebSocket(roomSlug, token);

      // Wait for WebSocket to be ready
      if (wsReadyPromise) {
        await wsReadyPromise;
      }
    }

    // Send message via WebSocket
    if (wsInstance && wsInstance.readyState === WebSocket.OPEN) {
      wsInstance.send(
        JSON.stringify({
          text_content: text_content,
        })
      );
    } else {
      throw new Error("WebSocket connection not ready");
    }
  },
};
